import React from 'react';
import clsx from 'clsx';
import { min } from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';

const legendDot =
  "before:content-[''] before:inline-block before:w-[10px] before:h-[10px] before:mr-2 before:rounded-full";

const barSeparator =
  "after:content-[''] after:absolute after:top-0 after:right-0 after:w-[2px] after:h-[30px] after:bg-white";

interface StatusBarProps {
  /** The maximum number of seats for the training. */
  total: number;

  /** The number of participants whose status is `invited`. */
  invited: number;

  /** The number of participants whose status is `registered`. */
  registered: number;

  /** The number of participants whose status is `pending`. */
  pending: number;
}

const StatusBar: React.FC<StatusBarProps> = ({
  total,
  invited,
  registered,
  pending
}) => {
  const registerWidth = min((registered / total) * 100, 100);
  const invitedWidth = min(((registered + invited) / total) * 100, 100);
  const pendingWidth = min(
    ((pending + registered + invited) / total) * 100,
    100
  );
  const available = total - invited - registered - pending;

  return (
    <div className="mb-4">
      <div className="relative w-full h-[30px] rounded bg-[#c3c3c3]">
        <div
          className={clsx(
            'absolute top-0 left-0 h-[30px] rounded-l bg-[#34495e]',
            pendingWidth === 100 ? 'rounded-r' : barSeparator
          )}
          style={{ width: `${pendingWidth}%` }}
        />
        <div
          className={clsx(
            'absolute top-0 left-0 h-[30px] rounded-l bg-[#da7e00]',
            invitedWidth === 100 ? 'rounded-r' : barSeparator
          )}
          style={{ width: `${invitedWidth}%` }}
        />
        <div
          className={clsx(
            'absolute top-0 left-0 h-[30px] rounded-l bg-[#0f7a6a]',
            registerWidth === 100 ? 'rounded-r' : barSeparator
          )}
          style={{ width: `${registerWidth}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap justify-end">
        <div className={clsx('ml-6 before:bg-[#0f7a6a]', legendDot)}>
          {i18n.ft(messages.registered, { number: registered })}
        </div>
        <div className={clsx('ml-6 before:bg-[#da7e00]', legendDot)}>
          {i18n.ft(messages.invited, { number: invited })}
        </div>
        <div className={clsx('ml-6 before:bg-[#34495e]', legendDot)}>
          {i18n.ft(messages.pending, { number: pending })}
        </div>
        <div className={clsx('ml-6 before:bg-[#c3c3c3]', legendDot)}>
          {i18n.ft(messages.total, { available, total })}
        </div>
      </div>
    </div>
  );
};

export default StatusBar;
